import React, { FunctionComponent, useEffect, useState } from "react";
import { Form, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { hideConfirmDialog } from "../../../store/confirm-dialog/actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { updateDoNotShowMeAgain } from "../../../store/auth/actions";

type Props = {};

const ConfirmDialog: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const [timeLeft, setTimeLeft] = useState(0);
  const [showMeAgain, setShowMeAgain] = useState<boolean>(false);
  const {
    show,
    onConfirm,
    onCancel,
    intent,
    title,
    centered,
    text,
    showLastStudentWarning,
    timerElipseLeft,
    isFinished,
    countDownTimer,
    cancelButtonText,
    confirmButtonText,
    size,
    backdropClassName,
    modalClassName,
    additionalButtonText,
    isAdditionalButton,
    popupKey,
    isCustomisedContent
  } = useSelector((s: ApplicationState) => s.confirmDialog.confirmDialog);

  useEffect(() => {
    if(show) {
      setShowMeAgain(false);
    }
  },[show])

  const currentLoggedInUserId = useSelector((s: ApplicationState) => s.auth.userInfo?.id);

  const handleDialogHide = () => {
    dispatch(hideConfirmDialog());
    onCancel && onCancel();
  };

  const handleConfirm = (buttonType?: boolean) => {
    dispatch(hideConfirmDialog());
    if(showMeAgain) {
      // Dispatch an action to save the DoNotShowMeAgain state
      dispatch(updateDoNotShowMeAgain({
        user_id: currentLoggedInUserId!,
        popup_key: popupKey ?? "Confirm Dialog",
        dismiss_until: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 100).toISOString() // 100 years from now in ISO 8601 format
      }));
    }
    onConfirm && onConfirm(buttonType);
  };

  useEffect(() => {
    if(countDownTimer) {
      setTimeLeft(countDownTimer!)
    }
  }, [countDownTimer, showLastStudentWarning, timerElipseLeft])

  useEffect(() => {
     if(showLastStudentWarning || timerElipseLeft) {
      // exit early when we reach 0
      if (!timeLeft) return;
  
      // save intervalId to clear the interval when the
      // component re-renders
      const intervalId = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
  
      // clear interval on re-render to avoid memory leaks
      return () => {
        clearInterval(intervalId);
      };
      // add timeLeft as a dependency to re-rerun the effect
      // when we update it
    }
  }, [timeLeft]);

  const handleShowMeAgain = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowMeAgain(e.target.checked);
  };

   return (
    <Modal
      className={`${modalClassName ?? ""} showConfirmDialog`}
      onHide={handleDialogHide}
      animation={false}
      centered={centered ?? true}
      backdropClassName={`customDarkModalBackdrop inner in ${backdropClassName ??
        ""}`}
      show={show}
      size={size}
    >
      <Modal.Header
        closeButton
        className="purpleModalHeader"
        style={{ borderBottom: 0 }}
      >
        <Modal.Title>{title ?? "Confirm Deletion"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-0">
        {showLastStudentWarning ? 
          <div className="alert alert-warning text-center">
            You are removing the last student from this group. Once moved, 
            the group will be {isFinished ? <span className="font-weight-bold"> archived</span>: <span className="font-weight-bold"> discarded</span>}.  
          </div> : ''}
        {isCustomisedContent
        ? text ?? "Are you sure? This operation cannot be undone."
        : <p className="text-center">
          {text ?? "Are you sure? This operation cannot be undone."}
        </p>
        }
        <hr />
        {popupKey ? (
          <Form.Check
            className="font-weight-bold d-flex align-items-center cursor-pointer"
            checked={showMeAgain}
            onChange={(e) => handleShowMeAgain(e)}
            custom
            type="checkbox"
            id="showMeAgainCheckbox"
            label={
              <label htmlFor="showMeAgainCheckbox" style={{ cursor: "pointer", marginBottom: 0, width: "100%" }}>
                Do not show me this again
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={
                    <Tooltip className="customTooltip" id="tooltip-top">
                      If this pop-up is updated, it will reappear.
                    </Tooltip>
                  }
                >
                  <span className="ml-2" style={{ cursor: "pointer" }}>
                    <FontAwesomeIcon icon={faQuestionCircle} />
                  </span>
                </OverlayTrigger>
              </label>
            }
          />
        ) : ''}
      </Modal.Body>
      <Modal.Footer>
        {isAdditionalButton 
        ?
            <div className="btnActions">
              <div>
                <button className="whiteBtnSm" onClick={handleDialogHide}>
                  {cancelButtonText ?? "Cancel"}
                </button>
              </div>
              <div className="d-flex">
                <button className="blueBtnSm " disabled={showLastStudentWarning || timerElipseLeft ? !!timeLeft : false} onClick={() => handleConfirm(true)}>
                  {showLastStudentWarning || timerElipseLeft ? !timeLeft ? additionalButtonText ?? "Yes, delete it" : timeLeft : additionalButtonText ?? "Yes, delete it"}
                </button>
                <button className="blueBtnSm ml-2" disabled={showLastStudentWarning || timerElipseLeft ? !!timeLeft : false} onClick={() => handleConfirm(false)}>
                  {showLastStudentWarning || timerElipseLeft ? !timeLeft ? confirmButtonText ?? "Yes, delete it" : timeLeft : confirmButtonText ?? "Yes, delete it"}
                </button>
              </div>
            </div>
        :
          <div className="btnActions">
            <button className="whiteBtnSm" onClick={handleDialogHide}>
              {cancelButtonText ?? "Cancel"}
            </button>
            <button className="blueBtnSm text-right" disabled={showLastStudentWarning || timerElipseLeft ? !!timeLeft : false} onClick={() => handleConfirm(false)}>
              {showLastStudentWarning || timerElipseLeft ? !timeLeft ? confirmButtonText ?? "Yes, delete it" : timeLeft : confirmButtonText ?? "Yes, delete it"}
            </button>
          </div>
        }
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
